import { useCallback, useEffect, useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { Button, Card, Grid } from '@mui/material'
import UploadIcon from '@mui/icons-material/Upload'
import dayjs, { Dayjs } from 'dayjs'
import OutboxIcon from '@mui/icons-material/Outbox'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import { message } from 'antd'

import Helmet from '../../../../../../components/Helmet'
import HeaderContent from '../../../../../../components/Layouts/HeaderContent'
import { ContentWrapper } from '../../../../../../components/ContentWrapper'
import { LeadImportCustomerRegisterFormModal } from './components/LeadImportFormModal'
import useAxios, { axiosInstance } from '../../../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../../../types/pagination'
import LeadListFilter, { IFilterMapped } from './components/LeadListFilter'
import LeadListTable from './components/LeadListTable'
import PageUrls from '../../../../../../constants/page-urls'
import { SendLeadFormModal } from '../../components/SendLeadFormModal'
import { MoveLeadFormModal } from '../../components/MoveLeadFormModal'
import { ECanceledAtStatus, ILead } from '../../../../../../types/lead'
import DeleteIcon from '@mui/icons-material/Delete'
import { MAX_DONWLOAD_PAGE_SIZE } from '../../../../../../constants/global'
import * as XLSX from 'xlsx'
import ConfirmCancelModal from './components/ConfirmCancelModal'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../constants/errors'

const Menus: any[] = []

const LeadListPage = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // อ่าน query params จาก location เพื่อให้ update เมื่อ URL เปลี่ยน
  const queryStr = useMemo(
    () =>
      queryString.parse(location.search, {
        arrayFormat: 'bracket',
      }),
    [location.search],
  )

  // อ่าน phones จาก query params
  const phonesFromQuery = useMemo(() => {
    return queryStr.phones
      ? Array.isArray(queryStr.phones)
        ? queryStr.phones
        : [queryStr.phones]
      : undefined
  }, [queryStr.phones])

  const [selectedLeads, setSelectedLeads] = useState<ILead[]>([])
  const [
    isModalImportCustomerRegisterLead,
    setIsModalImportCustomerRegisterLead,
  ] = useState<boolean>(false)
  const [isModalSendLead, setIsModalSendLead] = useState<boolean>(false)
  const [isModalMoveLead, setIsModalMoveLead] = useState<boolean>(false)
  const [isConfirmModal, setIsConfirmModal] = useState<boolean>(false)
  const [searchFilter, setSearchFilter] = useState<IFilterMapped>({
    page: Number(queryStr.page ?? 1),
    perPage: 15,
  })
  const [filter, setFilter] = useState<IFilterMapped>({
    page: Number(queryStr.page ?? 1),
    perPage: Number(queryStr.perPage ?? 15),
    search: queryStr.search?.length
      ? String(queryStr.search).toLowerCase().trim()
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : undefined,
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).endOf('day').format()
      : undefined,
  })

  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      setFilter(newFilter)
    },
    [setFilter],
  )

  const handleSearch = useCallback(
    (newFilter: IFilterMapped) => {
      const filter = {
        ...newFilter,
        page: 1,
      }
      setFilter(filter)
      setSearchFilter(filter)
      const queryParams: any = { ...filter }
      // เก็บ phones ไว้ใน URL ถ้ามี
      if (phonesFromQuery) {
        queryParams.phones = phonesFromQuery
      }
      const newQuery = queryString.stringify(queryParams, {
        arrayFormat: 'bracket',
      })
      navigate(`${PageUrls.CRM.Lead.List}?${newQuery}`, {
        replace: true,
      })
    },
    [navigate, phonesFromQuery],
  )

  const handlePageChange = useCallback(
    (newFilter: IFilterMapped) => {
      setFilter(newFilter)
      setSearchFilter(newFilter)
      const queryParams: any = { ...newFilter }
      // เก็บ phones ไว้ใน URL ถ้ามี
      if (phonesFromQuery) {
        queryParams.phones = phonesFromQuery
      }
      const newQuery = queryString.stringify(queryParams, {
        arrayFormat: 'bracket',
      })
      navigate(`${PageUrls.CRM.Lead.List}?${newQuery}`, {
        replace: true,
      })
    },
    [navigate, phonesFromQuery],
  )

  useEffect(() => {
    // ตรวจสอบว่ามี phones ใน URL หรือไม่ ถ้ามีก็ไม่ต้องเรียก handleSearch
    // เรียก handleSearch เฉพาะเมื่อไม่มี phones ใน URL และยังไม่มี filter อื่นๆ
    const hasOtherFilters = queryStr.search || queryStr.startDate || queryStr.endDate || queryStr.page
    if (!phonesFromQuery && !hasOtherFilters) {
      handleSearch(searchFilter)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const params = {
    ...searchFilter,
    expiredDateStatus:
      (searchFilter?.expiredDateStatus as any) == 'all'
        ? undefined
        : searchFilter.expiredDateStatus,
    canceledAtStatus: ECanceledAtStatus.NOT_CANCELED,
    // เพิ่ม phones ใน params ถ้ามี
    ...(phonesFromQuery && { phones: phonesFromQuery }),
  }

  const leadsList = useAxios<PaginationResponseInterface<ILead>>({
    method: 'GET',
    url: '/lead',
    params,
  })

  const handleOnDownload = useCallback(
    async (date: Dayjs) => {
      let loop = true
      let page = 1,
        perPage = MAX_DONWLOAD_PAGE_SIZE
      const data: ILead[] = []

      let startDate = params?.startDate
      let endDate = params?.endDate

      if (
        !startDate ||
        !endDate ||
        dayjs(endDate).diff(dayjs(startDate), 'day') > 31
      ) {
        startDate = date.startOf('month').format('YYYY-MM-DD')
        endDate = date.endOf('month').format('YYYY-MM-DD')
      }

      while (loop) {
        try {
          const result = await axiosInstance.get('/lead', {
            params: {
              ...params,
              page,
              perPage,
              startDate,
              endDate,
            },
          })
          page++
          if (!result?.data?.records.length) {
            loop = false
            break
          }
          data.push(...result.data.records)
          if (result?.data?.count < perPage) {
            loop = false
            break
          }
        } catch (e) {
          message.warning('เกิดข้อผิดพลาดในการดาวน์โหลดข้อมูลบางส่วน')
          break
        }
      }
      if (!data.length) {
        message.warning('ไม่พบข้อมูล')
        return
      }
      const rows = data?.map((lead: ILead) => {
        return {
          'ชื่อ-นามสกุล': lead?.customer?.name,
          ช่องทางการขาย: lead?.distribution?.name,
          ที่อยู่: `${lead?.customer?.address} ${lead?.customer?.subDistrict} ${lead?.customer?.district} ${lead?.customer?.province} ${lead?.customer?.zipcode}`,
          หมายเลขโทรศัพท์: lead?.customer?.phone,
          ผู้ดูแล: lead?.assignTo?.name,
          PIPELINE: lead?.pipelineName,
          สถานะ: lead?.activePipelineItemName,
          วันหมดอายุ: lead?.expiredAt
            ? dayjs(lead?.expiredAt).format('DD/MM/YYYY')
            : '-',
          สร้างเมื่อ: lead?.createdAt
            ? dayjs(lead?.createdAt).format('DD/MM/YYYY')
            : '-',
          แก้ไขเมื่อ: lead?.updatedAt
            ? dayjs(lead?.updatedAt).format('DD/MM/YYYY')
            : '-',
        }
      })
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, `lead.xlsx`)
      message.success('ดาวน์โหลดสำเร็จ')
    },
    [params],
  )

  const onHandleCancelLead = async (leads: ILead[], note: string) => {
    try {
      await axiosInstance.delete(`/lead/cancel-leads`, {
        data: {
          objectIds: leads.map((lead) => lead.objectId),
          note,
        },
      })
      message.success(`ยกเลิกข้อมูลสำเร็จ`)
      onUpdateLeadListSuccess()
      setIsConfirmModal(false)
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const onUpdateLeadListSuccess = async () => {
    try {
      setSelectedLeads([])
      await leadsList.mutate()
    } catch (error) {
      message.error('ไม่สามารถดึงข้อมูล Lead Pipeline ได้')
    }
  }

  return (
    <ContentWrapper>
      <Helmet title="รายการติดตามทั้งหมด" />
      <ConfirmCancelModal
        open={isConfirmModal}
        onClose={() => setIsConfirmModal(false)}
        onConfirm={(note) => onHandleCancelLead(selectedLeads, note)}
        title={'ยืนยันการยกเลิกการติดตาม'}
        message={'คุณต้องการยกเลิกการติดตาม Lead ที่เลือกหรือไม่?'}
      />
      <LeadImportCustomerRegisterFormModal
        isOpen={isModalImportCustomerRegisterLead}
        onClose={(visible: boolean) =>
          setIsModalImportCustomerRegisterLead(visible)
        }
        onUpdateLeadListSuccess={onUpdateLeadListSuccess}
      />
      <SendLeadFormModal
        objectIds={selectedLeads.map((lead) => lead.objectId!)}
        isOpen={isModalSendLead}
        onClose={(visible: boolean) => setIsModalSendLead(visible)}
        onUpdateLeadListSuccess={onUpdateLeadListSuccess}
      />
      <MoveLeadFormModal
        objectIds={selectedLeads.map((lead) => lead.objectId!)}
        isOpen={isModalMoveLead}
        onClose={(visible: boolean) => setIsModalMoveLead(visible)}
        onUpdateLeadListSuccess={onUpdateLeadListSuccess}
      />
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <HeaderContent
            goBack
            title="รายการติดตามทั้งหมด"
            routes={Menus}
            descriptions="รายการติดตามทั้งหมด"
            tags={[
              {
                title: 'จำนวนทั้งหมด',
                color: 'info',
                value: leadsList.data?.count ?? 0,
                classifier: 'รายการ',
              },
            ]}
          />
        </Grid>
        <Grid item mb={2}>
          <Button
            variant="contained"
            size="large"
            startIcon={<UploadIcon />}
            color={'info'}
            onClick={() => setIsModalImportCustomerRegisterLead(true)}
          >
            นำเข้าทะเบียนลูกค้า
          </Button>
        </Grid>
      </Grid>
      <Card
        sx={{
          padding: 2,
        }}
      >
        <LeadListFilter
          filter={filter}
          onFilterChange={onFilterChange}
          onSearch={handleSearch}
          onDownload={handleOnDownload}
        />
        {selectedLeads.length > 0 && (
          <Grid container spacing={2} mb={2}>
            {!selectedLeads.filter((lead) => lead.isFinished).length && (
              <Grid item>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<OutboxIcon />}
                  color={'info'}
                  onClick={() => setIsModalSendLead(true)}
                >
                  ส่งต่อ Lead {`(${selectedLeads.length})`}
                </Button>
              </Grid>
            )}

            <Grid item>
              <Button
                variant="contained"
                size="medium"
                startIcon={<DriveFileMoveIcon />}
                color={'warning'}
                onClick={() => setIsModalMoveLead(true)}
              >
                ย้าย Pipeline {`(${selectedLeads.length})`}
              </Button>
            </Grid>

            {!selectedLeads.filter((lead) => lead.isFinished).length && (
              <Grid item>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<DeleteIcon />}
                  color={'error'}
                  onClick={() => setIsConfirmModal(true)}
                >
                  ยกเลิก การติดตาม{`(${selectedLeads.length})`}
                </Button>
              </Grid>
            )}
          </Grid>
        )}
        <LeadListTable
          selectedData={selectedLeads}
          setSelectedData={(leads) => setSelectedLeads(leads || [])}
          isLoading={leadsList.isLoading}
          data={leadsList.data?.records}
          onUpdatePipelineListSuccess={onUpdateLeadListSuccess}
          filter={filter}
          onSearch={handleSearch}
          handlePageChange={handlePageChange}
          total={leadsList.data?.count ?? 0}
          onMoveLead={(lead: ILead) => {
            setSelectedLeads([lead])
            setIsModalMoveLead(true)
          }}
          onSendLead={(lead: ILead) => {
            setSelectedLeads([lead])
            setIsModalSendLead(true)
          }}
        />
      </Card>
    </ContentWrapper>
  )
}

export default LeadListPage
